import { useCallback, useEffect, useRef, useState } from "react";
import {
  assembleJournalTextFor,
  emptyJournalHeadingsFor,
  isKeepWishOpen,
  isLegacyDoseLabel,
  monthlyReviewNeedsLabOrder,
  MONTHLY_JOURNAL_LAYOUT,
  nextFollowUpLine,
  PUBLISH_TO_PATIENT_CTA,
  renewalDoingWellLabel,
  type ClinicalPhase,
  type GoalZoneDoctorSummary,
  type MeetingPrescriptionOrder,
  type MonthlyReviewDecisions,
  type RenewalAnswers,
  type UpdateJournalSections,
  type WebdocControl,
  monthlyJournalDecision,
  prescriptionLabel,
  type MonthlyDiscussion,
  type MonthlyFollowUp,
  type PrescriptionDecision,
  type TreatmentStartState,
  finalizeTreatmentComment,
  treatmentCommentPrefill,
} from "@care-by-ella/contracts";
import { doctorNoticeText } from "@/lib/doctor-notice";
import { renewalSymptomLine } from "@/lib/symptom-lines";
import { monthlyCommentOutcome } from "@/lib/treatment-comment";
import { TreatmentCommentField } from "@/components/TreatmentCommentField";
import {
  MONTHLY_OUTCOME_LABEL,
  MONTHLY_OUTCOMES,
  monthlyDecisionBlocker,
  monthlyOutcomeEffects,
  type MonthlyOutcome,
} from "@/lib/monthly-decision";
import { DecisionSection } from "./DecisionSection";
import {
  MonthlyDiscussionCard,
  MonthlyFollowUpQuestions,
} from "./MonthlyCaseDiscussion";
import { TreatmentStatusChain } from "./TreatmentStatusChain";
import {
  confirmedNoteStillCurrent,
  noteInputChanged,
  renewalWebdocBlocker,
  renewalWebdocControl,
} from "@/lib/renewal-webdoc";
import {
  AiSummarisedHeadings,
  JournalSectionsCard,
  type JournalSectionsHandle,
  type JournalValues,
} from "@/components/JournalSectionsCard";
import { autoSummaryProgressLabel } from "@/lib/journal-auto-summary";
import {
  MonthlyCompletionSection,
  NextFollowUpSection,
} from "@/components/MonthlyReviewDecisions";
import {
  CLINICAL_PHASE_LABEL,
  ClinicalPhaseField,
} from "@/components/ClinicalPhaseField";
import {
  monthlyPhaseEmptyHint,
  monthlyPhaseEmptyOption,
  monthlyPhaseHint,
  phaseDecisionMissing,
} from "@/lib/monthly-phase-decision";
import {
  EMPTY_PRESCRIPTION_CHOICE,
  prescriptionDecisionPayload,
  WebdocControlStep,
  type PrescriptionChoiceValue,
} from "@/components/WebdocControlStep";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  EMPTY_ORDER,
  orderDraftFrom,
  type OrderDraft,
  orderComplete,
  PrescriptionOrderFields,
  toOrder,
} from "@/components/PrescriptionOrderFields";
import {
  adjustDose,
  approveRenewal,
  fetchMonthlyReviewDecisions,
  fetchMonthlyReviewJournal,
  markLabNotNeeded,
  publishReviewFeedback,
  rejectRenewal,
  requestFollowUp,
  saveMonthlyReviewJournal,
} from "@/lib/work-queue-api";
import { useReadOnly } from "./SupportView";

// TES-519 — the renewal decision lives on the PATIENT screen now (the
// "Månadsincheckning" tab), not embedded in a work-queue row. Every
// lane row lands on the patient screen with the content up
// and one clear CTA. Lifted out of WorkQueue.tsx unchanged; the one-step
// "Skicka återkoppling och förnya" from TES-506 is still the primary action.

// TES-495 — the renewal check-in's `doingWell` is stored as a raw categorical
// ('yes' | 'partly' | 'no', deliberately a free string in the schema so the
// vocabulary can widen without a migration). The doctor reads Swedish, not
// enum values: renewalDoingWellLabel (contracts) is shared with the monthly
// journal's prefill so the two cannot word her answer differently.

// TES-290 — the doctor decision panel. Approve (with dose +
// needs-new-labs), reject (with reason), mark labs not-needed, or request a
// follow-up. Each button drives one of the new decision endpoints and then
// reloads the queue.
//
// TES-1291 (TES-1267 §4) — the decision is made in the same six sections as
// every other treatment decision (DecisionSection): current treatment and
// her new answers, a structured outcome (renewal, follow-up only, dose
// change, rejection), Recept, the doctor's comment, Webdoc, and a preview
// before the one publish button. The outcomes send exactly the requests the
// old buttons did (lib/monthly-decision.ts). The ten-heading journal stays
// as it was.
export function RenewalReviewPanel({
  patientId,
  cycleId,
  renewal,
  currentOrder,
  goalZone,
  treatmentStartState,
  monthlyCheckinMissing,
  monthlyDiscussion,
  followUp,
  onDone,
  onError,
  onUnauthorized,
}: {
  patientId: string;
  cycleId: string;
  renewal: RenewalAnswers | null;
  /** TES-1291 — for the status chain at the top, as in the decision workspace. */
  treatmentStartState?: TreatmentStartState | null;
  /** TES-1011 — her goal-zone state; an open keep-wish makes the phase required. */
  goalZone?: GoalZoneDoctorSummary | null;
  /** TES-1431 — her due F-05 while it is unsubmitted; the renewal waits for it. */
  monthlyCheckinMissing?: { monthIndex: number; dueAt: string } | null;
  /** TES-1637 — her discussion answers from her latest F-05. */
  monthlyDiscussion?: MonthlyDiscussion | null;
  /** TES-1637 — where the case's follow-up question stands. */
  followUp?: MonthlyFollowUp | null;
  /** Current prescription, for pre-filling. Null for cycles from before TES-636. */
  currentOrder: {
    medication: string | null;
    dose: string | null;
    frequency: string | null;
    duration?: string | null;
  } | null;
  onDone: () => void;
  onError: (e: unknown) => void;
  onUnauthorized: () => void;
}) {
  const readOnly = useReadOnly();
  // TES-636 — the whole prescription, not just the strength. Pre-filled with
  // the one in force now: an unchanged renewal should be a confirmation, not a
  // transcription. The fields are still required — what was removed is the
  // possibility of approving WITHOUT having seen the prescription.
  // TES-702 — an old label is NOT pre-filled.
  //
  // Cycles from before TES-636 have the whole prescription in `dose`
  // ("Testpreparat 0,25 mg/vecka") and neither medication nor frequency. That
  // string does not pass the dose validation, so pre-filling it would have
  // given the doctor a field that looks filled in and that the form then
  // refuses to send — an error she neither wrote nor can understand. If the
  // row is old we start from empty fields; the old label is shown instead, as
  // a reference, above.
  const legacy = isLegacyDoseLabel({
    medication: currentOrder?.medication ?? null,
    dose: currentOrder?.dose ?? null,
  });
  const [order, setOrder] = useState<OrderDraft>(() =>
    legacy ? EMPTY_ORDER : orderDraftFrom(currentOrder ?? {}),
  );
  // TES-1291 — the review's outcome. Nothing preselected: each is a clinical
  // decision.
  const [outcome, setOutcome] = useState<MonthlyOutcome | null>(null);
  const [reason, setReason] = useState("");
  const [needsNewLabs, setNeedsNewLabs] = useState(false);
  // TES-1290 — "Läkarens kommentar till behandlingsbeslutet" replaces the
  // free "Månadsåterkoppling". Null until the doctor types in it: it then
  // follows the prefill for the chosen outcome.
  const [comment, setComment] = useState<string | null>(null);
  // TES-997 (Ö-255) — the same three prescription choices every month,
  // nothing preselected, and "Befintligt recept gäller till" as a date.
  const [prescription, setPrescription] = useState<PrescriptionChoiceValue>(
    EMPTY_PRESCRIPTION_CHOICE,
  );
  const prescriptionPayload = prescriptionDecisionPayload(prescription);
  const [busy, setBusy] = useState(false);
  // TES-1011 — the review's phase decision. No default (TES-841): null until
  // the doctor picks, and sent only when picked.
  const [phase, setPhase] = useState<ClinicalPhase | null>(null);
  const keepWishOpen = isKeepWishOpen(goalZone?.confirmation);

  // TES-904 step 4 / TES-978 — the monthly review's journal note: its own ten
  // headings, in the care meeting's box model, on the cycle under review.
  const journalRef = useRef<JournalSectionsHandle>(null);
  const loadJournal = useCallback(
    () => fetchMonthlyReviewJournal(patientId, cycleId),
    [patientId, cycleId],
  );
  const saveJournal = useCallback(
    (input: UpdateJournalSections) =>
      saveMonthlyReviewJournal(patientId, cycleId, input),
    [patientId, cycleId],
  );

  // TES-1034 (Emelie, point 1) — no free-standing "Stödord" box and no
  // "Sammanställ journal" on the card: Kirill writes under the headings, and
  // the Webdoc step below compiles, copies and confirms the note. Its final
  // version and receipt are stored by the decision itself.
  // TES-1312 — the AI run before the note is composed, and the headings it
  // wrote.
  const [summaryProgress, setSummaryProgress] = useState<string | null>(null);
  const [aiHeadings, setAiHeadings] = useState<string[]>([]);
  const [webdocNote, setWebdocNote] = useState<{
    text: string;
    skipped: string[];
  } | null>(null);
  // The boxes as saved when the note was last compiled (TES-1063).
  const [webdocValues, setWebdocValues] = useState<JournalValues | null>(null);
  const [journalDone, setJournalDone] = useState(false);
  const [labOrderDone, setLabOrderDone] = useState(false);
  // TES-442 — "Remissnummer i Webdoc (valfritt)" beside the remiss tick.
  const [requisitionRef, setRequisitionRef] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const [noteChanged, setNoteChanged] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // TES-978 — the review's decisions beside the renewal: the komplettering
  // and "Nästa uppföljning". Without them the rest of the review still works;
  // the two sections just do not show.
  const [decisions, setDecisions] = useState<MonthlyReviewDecisions | null>(
    null,
  );
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  useEffect(() => {
    let live = true;
    fetchMonthlyReviewDecisions(patientId, cycleId)
      .then((d) => {
        if (live) setDecisions(d);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      });
    return () => {
      live = false;
    };
  }, [patientId, cycleId]);

  // The order on screen, as the note's decision box is assembled with it and
  // as the API assembles the stored version (`journalOrder`).
  const journalOrder: MeetingPrescriptionOrder = {
    medication: order.medication.trim(),
    dose: order.dose.trim(),
    frequency: order.frequency.trim(),
    duration: order.duration.trim(),
  };
  const followUpLine = decisions?.nextFollowUp
    ? nextFollowUpLine(decisions.nextFollowUp)
    : null;

  // Assembles the note exactly as the API will store it: the saved boxes,
  // the order on screen leading "Åtgärd och beslut", the follow-up leading
  // "Nästa uppföljning".
  function noteFrom(values: JournalValues): {
    text: string;
    skipped: string[];
  } {
    const sections = MONTHLY_JOURNAL_LAYOUT.keys.map((key) => ({
      key,
      content: values[key] ?? "",
    }));
    // TES-1058 — the prescription status chosen in the Webdoc step and the
    // komplettering this review asked for, as the API compiles them.
    const opts = {
      order: journalOrder,
      followUp: followUpLine,
      decision: monthlyJournalDecision({
        prescription: prescriptionPayload,
        // TES-1095 (rule 3) — as the API compiles it: the order she is on
        // against the order on screen.
        orders: { current: currentOrder, onScreen: journalOrder },
        completion: decisions?.completion ?? null,
      }),
    };
    return {
      text: assembleJournalTextFor(MONTHLY_JOURNAL_LAYOUT, sections, opts),
      skipped: emptyJournalHeadingsFor(MONTHLY_JOURNAL_LAYOUT, sections, opts),
    };
  }

  // Saves what is unsaved in the boxes, then assembles the note from them.
  // The saved boxes are kept, so the note can follow the step's other
  // inputs without saving again.
  async function compileNote(): Promise<{ text: string; skipped: string[] }> {
    const values = (await journalRef.current?.flush()) ?? {};
    setWebdocValues(values);
    return noteFrom(values);
  }

  // TES-1063 — the note as it stands now, from the boxes saved when the step
  // compiled it and the inputs on screen (the prescription status above
  // all, since TES-1058 writes it into the note). When it moves away from
  // the note shown, the step shows the new text at once and, if the old one
  // was already copied or ticked, takes the tick back and says so — instead
  // of the doctor finding out only when she presses publish.
  const liveNote = webdocValues ? noteFrom(webdocValues) : null;
  const liveText = liveNote?.text ?? null;
  useEffect(() => {
    if (!liveNote) return;
    const change = noteInputChanged(webdocNote?.text ?? null, liveNote.text, {
      journalDone,
      copied: copyState !== "idle",
    });
    if (!change) return;
    setWebdocNote(liveNote);
    if (change.reconfirm) {
      setJournalDone(false);
      setCopyState("idle");
      setNoteChanged(true);
    }
    // Re-run only when the text itself changes; the ticks are read as they
    // are. (react-hooks/exhaustive-deps is not enabled in apps/admin.)
  }, [liveText]);

  // TES-1312 — headings still holding her raw answers are summarised by the
  // AI first; if any stays raw the card says so and nothing is composed
  // until the doctor chooses ("Sammanställ ändå" comes back with `force`).
  // The monthly review's headings have no AI route, so an untouched prefill
  // here always gets the notice.
  async function openWebdocStep(force = false) {
    setBusy(true);
    try {
      if (!force) {
        const gate = await journalRef.current?.summariseRaw((current, total) =>
          setSummaryProgress(autoSummaryProgressLabel(current, total)),
        );
        setSummaryProgress(null);
        if (gate && gate.raw.length > 0) return;
      }
      setWebdocNote(await compileNote());
      setAiHeadings(journalRef.current?.aiSummarisedHeadings() ?? []);
      setJournalDone(false);
      setLabOrderDone(false);
      setCopyState("idle");
      setNoteChanged(false);
    } catch {
      // The card says why the save failed; the step stays closed.
    } finally {
      setSummaryProgress(null);
      setBusy(false);
    }
  }

  // "Kopiera hela journalanteckningen". Without clipboard access the text is
  // selected instead, for the doctor to copy by hand.
  async function copyNote() {
    if (!webdocNote?.text) return;
    try {
      await navigator.clipboard.writeText(webdocNote.text);
      setCopyState("copied");
    } catch {
      noteRef.current?.focus();
      noteRef.current?.select();
      setCopyState("failed");
    }
  }

  // TES-1034 (Emelie, point 2) — the remiss row, only when the review's
  // komplettering asked for a new blood test.
  const needsLabOrder = monthlyReviewNeedsLabOrder(decisions?.completion);
  const webdocState = {
    note: webdocNote?.text ?? null,
    journalDone,
    needsLabOrder,
    labOrderDone,
    requisitionRef,
  };
  const webdocBlocker = renewalWebdocBlocker(webdocState);

  // Every decision in the review: save the note, check that the note the
  // doctor confirmed is still the note as it stands, then send the
  // confirmation with the decision. A note that changed since the step
  // compiled it is shown again and must be confirmed again: the tick covered
  // the old text. Since TES-1063 the step already does this the moment an
  // input on screen changes; what reaches this backstop is a journal box
  // edited after the step compiled.
  // `gated: false` is for the actions that decide nothing in the review (a
  // dose correction, a note, labs not needed, a coach follow-up).
  async function run(
    fn: (webdoc: {
      webdocControl: WebdocControl;
      journalOrder: MeetingPrescriptionOrder;
      journalPrescription?: PrescriptionDecision;
    }) => Promise<unknown>,
    gated = true,
  ) {
    setBusy(true);
    // Every action reloads the patient screen, and a decided review takes
    // this panel away: save what is in the journal first. A failed save stops
    // here, with the card showing why, rather than losing the text.
    let current: { text: string; skipped: string[] };
    try {
      current = await compileNote();
    } catch {
      setBusy(false);
      return;
    }
    if (
      gated &&
      !confirmedNoteStillCurrent(webdocNote?.text ?? null, current.text)
    ) {
      setWebdocNote(current);
      setJournalDone(false);
      setLabOrderDone(false);
      setCopyState("idle");
      setNoteChanged(true);
      setBusy(false);
      return;
    }
    try {
      await fn({
        webdocControl: renewalWebdocControl(webdocState),
        journalOrder,
        // TES-1058 — the note's "Receptstatus", read only into the note on
        // a rejection or a feedback publish.
        ...(prescriptionPayload
          ? { journalPrescription: prescriptionPayload }
          : {}),
      });
      onDone();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  const orderReady = orderComplete(order);
  const trimmedReason = reason.trim();
  // TES-1290 — a dose change, or a renewal or follow-up whose order differs
  // from the one in force, gets the dose-change prefill; a rejection
  // publishes no comment.
  const commentPrefill = treatmentCommentPrefill(
    outcome === "reject"
      ? null
      : outcome === "dose_change"
        ? "dose_changed"
        : monthlyCommentOutcome(legacy ? null : currentOrder, order),
  );
  const commentText = doctorNoticeText(comment, commentPrefill);
  const trimmedSummary = finalizeTreatmentComment(commentText);
  const phaseMissing = phaseDecisionMissing(keepWishOpen, phase);
  const feedbackBody = {
    patientSummary: trimmedSummary,
    ...(phase ? { clinicalPhase: phase } : {}),
  };

  // TES-1291 — what stands in the way of the chosen outcome, and what it
  // changes for her.
  const blocker = monthlyDecisionBlocker({
    outcome,
    webdocBlocker,
    orderReady,
    commentGiven: trimmedSummary !== "",
    prescriptionChosen: prescriptionPayload !== null,
    phaseMissing,
    keepWishOpen,
    prescriptionChoice: prescription.choice,
    monthlyCheckinMissing: Boolean(monthlyCheckinMissing),
  });
  const effects = outcome
    ? monthlyOutcomeEffects(outcome, prescription.choice)
    : null;

  // The outcome's requests, exactly as the old buttons sent them. The
  // comment goes first (TES-506): if the renewal or the dose change then
  // fails, she has still had her answer and the doctor can redo the
  // decision; the other way round would leave a changed treatment with no
  // word to her.
  function publishOutcome() {
    if (!outcome) return;
    void run(async (webdoc) => {
      if (outcome === "reject") {
        await rejectRenewal(patientId, cycleId, {
          reason: trimmedReason || undefined,
          ...webdoc,
        });
        return;
      }
      await publishReviewFeedback(patientId, cycleId, {
        ...feedbackBody,
        ...webdoc,
      });
      if (outcome === "renew") {
        await approveRenewal(patientId, cycleId, {
          ...toOrder(order),
          needsNewLabs,
          prescription: prescriptionPayload!,
          webdocControl: webdoc.webdocControl,
        });
      } else if (outcome === "dose_change") {
        await adjustDose(patientId, cycleId, toOrder(order));
      }
    });
  }

  return (
    <div className="mt-md flex flex-col gap-sm border-t border-surfaceMuted pt-md">
      {/* TES-1291 — where she is in the chain, read-only, as at the top of
          the decision workspace. */}
      {treatmentStartState ? (
        <TreatmentStatusChain
          startState={treatmentStartState}
          plannedMedication={false}
        />
      ) : null}

      <DecisionSection section="current" testId={`renewal-${cycleId}`}>
        {currentOrder && !legacy && currentOrder.dose ? (
          <p className="text-sm">
            <span className="text-textSecondary">Nuvarande ordination: </span>
            <span className="font-semibold">
              {prescriptionLabel({
                medication: currentOrder.medication,
                dose: currentOrder.dose,
                frequency: currentOrder.frequency,
                duration: currentOrder.duration ?? null,
              })}
            </span>
          </p>
        ) : null}

        {/* TES-1431 (TES-1425 §6) — the renewal's evidence is missing. Said
            before her answers so the doctor knows before reading on. Words
            are Ö-790 (1), Emelie 2026-10-21, verbatim. */}
        {monthlyCheckinMissing ? (
          <p
            className="rounded-md border border-warning bg-surface p-sm text-sm text-textPrimary"
            data-testid={`renewal-${cycleId}-f05-missing`}
          >
            <span className="font-semibold">
              Månadsincheckning (F-05) saknas
            </span>
            {` · öppen sedan ${monthlyCheckinMissing.dueAt.slice(0, 10)}. `}
            Patienten behöver skicka in den inför bedömning av nästa
            receptförnyelse. Behandlingen avslutas inte automatiskt.
          </p>
        ) : null}

        {/* TES-1637 — what she wants to discuss, prominently with the
            monthly evidence, and the case's follow-up question. Neither
            holds the decision below back. */}
        <MonthlyDiscussionCard
          cycleId={cycleId}
          discussion={monthlyDiscussion}
        />
        <MonthlyFollowUpQuestions
          patientId={patientId}
          cycleId={cycleId}
          followUp={followUp}
          onUnauthorized={onUnauthorized}
        />

        {/* TES-495 — the patient's own answers first. The decision (dose, new
            samples, feedback) is made from them, so they belong above the
            fields and not on another page. */}
        {renewal ? (
          <div className="rounded-md bg-surfaceMuted/40 p-sm text-sm">
            <p className="text-xs uppercase tracking-wide text-textSecondary">
              Patientens svar · {renewal.submittedAt.slice(0, 10)}
            </p>
            <dl className="mt-xs grid grid-cols-[auto_1fr] gap-x-md gap-y-xs">
              <dt className="text-textSecondary">Vikt</dt>
              <dd>{renewal.currentWeightKg} kg</dd>
              <dt className="text-textSecondary">Hur det går</dt>
              <dd>{renewalDoingWellLabel(renewal.doingWell)}</dd>
              {/* TES-978:
                  "Saknad information får aldrig formuleras som ett normalt fynd."
                  An unanswered question is left out; "Inga angivna" read as if
                  she had said she had none. */}
              {renewal.sideEffects?.trim() ? (
                <>
                  <dt className="text-textSecondary">Biverkningar</dt>
                  <dd>{renewal.sideEffects}</dd>
                </>
              ) : null}
              {/* TES-1443 — the register symptoms she chose after her own
                  text, with their details; also in her symptom history. */}
              {renewal.symptoms.length > 0 ? (
                <>
                  <dt className="text-textSecondary">Valda besvär</dt>
                  <dd>
                    <ul
                      className="space-y-xs"
                      data-testid={`renewal-${cycleId}-symptoms`}
                    >
                      {renewal.symptoms.map((s) => (
                        <li key={s.symptomCode}>
                          <span>{renewalSymptomLine(s)}</span>
                          {s.note ? (
                            <span className="block text-textSecondary">
                              {s.note}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </>
              ) : null}
              {renewal.questionsForDoctor?.trim() ? (
                <>
                  <dt className="text-textSecondary">Frågor till läkaren</dt>
                  <dd>{renewal.questionsForDoctor}</dd>
                </>
              ) : null}
            </dl>
          </div>
        ) : (
          <p className="text-sm text-textSecondary">
            Patienten har inte skickat in någon förnyelsecheck-in för den här
            cykeln.
          </p>
        )}

        {/* TES-904 step 4 / TES-978 — prefilled from the answers above, her
            weekly check-ins, her readings, earlier F-01/F-02 information
            (TES-1034) and the order; the Webdoc step below adds the order in
            the fields below as the first line of "Åtgärd och beslut". */}
        <JournalSectionsCard
          ref={journalRef}
          idPrefix={`cycle-${cycleId}`}
          layout={MONTHLY_JOURNAL_LAYOUT}
          load={loadJournal}
          save={saveJournal}
          onUnauthorized={onUnauthorized}
          order={order}
          followUpLine={followUpLine}
          onCompileAnyway={() => void openWebdocStep(true)}
        />

        {/* TES-984 (Ö-268) — neither the renewal nor a dose change moves the
            clinical phase. TES-1011 — the phase decision below is the one way
            the monthly review changes it: explicit, published with the
            feedback. */}
        {decisions ? (
          <p className="text-sm text-textSecondary">
            <span className="font-semibold text-textPrimary">
              Klinisk behandlingsfas:
            </span>{" "}
            {decisions.clinicalPhase
              ? CLINICAL_PHASE_LABEL[decisions.clinicalPhase]
              : "Ingen fas vald"}
            <span className="block text-xs">
              Ändras bara genom fasbeslutet nedan, som publiceras med läkarens
              kommentar till behandlingsbeslutet.
            </span>
          </p>
        ) : null}

        {/* TES-1034 (Emelie, point 1) — a note written before the journal
            headings is shown read-only as history. */}
        {decisions?.legacyClinicalNote ? (
          <div className="rounded-md bg-surfaceMuted/40 p-sm text-sm">
            <p className="text-xs uppercase tracking-wide text-textSecondary">
              Tidigare klinisk anteckning (historik)
            </p>
            <p className="mt-xs whitespace-pre-wrap">
              {decisions.legacyClinicalNote}
            </p>
          </div>
        ) : null}
      </DecisionSection>

      {/* TES-1040 — the admin's read-only view stops at the answers, the
          note and the phase line above: everything below decides, sends or
          publishes. */}
      {readOnly ? null : (
        <>
          <DecisionSection section="outcome" testId={`renewal-${cycleId}`}>
            <fieldset className="flex flex-col gap-xs">
              <legend className="sr-only">Utfall</legend>
              {MONTHLY_OUTCOMES.map((o) => (
                <label
                  key={o}
                  className={`flex cursor-pointer items-start gap-sm rounded-md border p-sm text-sm ${
                    outcome === o
                      ? "border-brand bg-brand-accent"
                      : "border-surfaceMuted"
                  }`}
                >
                  <input
                    type="radio"
                    name={`renewal-outcome-${cycleId}`}
                    className="mt-[3px] accent-brand"
                    checked={outcome === o}
                    disabled={busy}
                    onChange={() => setOutcome(o)}
                  />
                  <span className="font-semibold">
                    {MONTHLY_OUTCOME_LABEL[o]}
                  </span>
                </label>
              ))}
            </fieldset>
            <p className="text-xs text-textSecondary">
              Uppehåll, avslut och läkemedelsbyte beslutas under
              Behandlingsbeslut.
            </p>

            {/* TES-978 — "kompletterings- och uppföljningsbeslut", in the same
                workspace as the dose and the prescription. */}
            {decisions ? (
              <>
                <MonthlyCompletionSection
                  patientId={patientId}
                  cycleId={cycleId}
                  decisions={decisions}
                  onSaved={setDecisions}
                  onUnauthorized={onUnauthorized}
                />
                <NextFollowUpSection
                  patientId={patientId}
                  cycleId={cycleId}
                  decisions={decisions}
                  onSaved={setDecisions}
                  onUnauthorized={onUnauthorized}
                />
              </>
            ) : null}

            {/* TES-1011 — the phase decision. Always offered; prominent and
                required while her keep-wish is open (the wish itself is shown
                above this panel). */}
            <div
              className={
                keepWishOpen
                  ? "rounded-md border border-brand/40 bg-brand/5 p-sm"
                  : undefined
              }
            >
              {keepWishOpen ? (
                <p className="mb-xs text-sm font-semibold text-textPrimary">
                  Fasbeslut om patientens önskemål (krävs)
                </p>
              ) : null}
              <ClinicalPhaseField
                idPrefix={`monthly-${cycleId}`}
                value={phase}
                onChange={setPhase}
                disabled={busy}
                hint={(p) =>
                  monthlyPhaseHint(p, {
                    keepWishOpen,
                    currentPhase: decisions?.clinicalPhase ?? null,
                  })
                }
                emptyOption={monthlyPhaseEmptyOption(keepWishOpen)}
                emptyHint={monthlyPhaseEmptyHint(keepWishOpen)}
              />
            </div>

            {/* TES-1034 (Emelie, point 1) — no free-standing clinical note:
                everything Kirill writes about the review goes under its
                journal heading above. This box is only the reason a
                rejection, a correction or a follow-up to the coach needs; it
                is never stored on the cycle as a note. */}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Skäl vid avslag, rättelse av ordination eller uppföljning till coach (förs inte in i journalen)"
              aria-label="Skäl"
              rows={2}
              className="rounded-md border border-surfaceMuted bg-background px-sm py-xs text-sm"
            />
          </DecisionSection>

          <DecisionSection section="recept" testId={`renewal-${cycleId}`}>
            <div className="flex flex-wrap items-end gap-sm">
              <PrescriptionOrderFields
                idPrefix={`order-${cycleId}`}
                value={order}
                onChange={setOrder}
                disabled={busy}
              />
              <label className="flex items-center gap-xs text-xs text-textSecondary">
                <input
                  type="checkbox"
                  checked={needsNewLabs}
                  onChange={(e) => setNeedsNewLabs(e.target.checked)}
                />
                Kräver nya prover
              </label>
            </div>
          </DecisionSection>

          {/* TES-1290 — the review's one patient text, "Läkarens kommentar
              till behandlingsbeslutet" (A-02 §2: "Ingen separat fritext ska
              skapas för medicinsk månadsåterkoppling, receptförnyelse,
              dosändring eller ändrad behandlingsplan"). Still published as
              the cycle's CP-33 notice, so Din behandling shows it as the
              version's comment and an app on the old contract reads it at
              medical-review/monthly/latest. The section heading names it,
              so the field's own label is for screen readers only. */}
          <DecisionSection section="comment" testId={`renewal-${cycleId}`}>
            <TreatmentCommentField
              id={`monthly-comment-${cycleId}`}
              text={commentText}
              value={comment}
              prefill={commentPrefill}
              onChange={setComment}
              structured={order}
              disabled={busy}
              labelHidden
            />
          </DecisionSection>

          <DecisionSection section="webdoc" testId={`renewal-${cycleId}`}>
            {/* TES-1034 (Emelie, points 2 and 3) — the compiled note, before
                anything is decided or published: shown read-only, copied
                whole, and confirmed as entered in Webdoc. Every decision
                below waits for it; the API refuses one without it and stores
                the note's final version and receipt with the decision. */}
            <div
              className="flex flex-col gap-xs"
              data-testid={`renewal-${cycleId}-webdoc-note`}
            >
              <button
                type="button"
                disabled={busy}
                onClick={() => void openWebdocStep()}
                className="self-start rounded-md border border-brand px-md py-sm text-sm font-semibold text-brand hover:bg-brand/5 disabled:opacity-50"
              >
                {/* TES-1294 — one label, A-02: "Det ska finnas en enda knapp
                    längst ned efter samtliga journalfält: SAMMANSTÄLL
                    JOURNALANTECKNING". */}
                {summaryProgress ?? "Sammanställ journalanteckning"}
              </button>
              {noteChanged ? (
                <p className="text-xs text-warning" aria-live="polite">
                  Journalanteckningen har ändrats sedan du bekräftade den.
                  Kopiera den nya texten till Webdoc och bekräfta igen.
                </p>
              ) : null}
              {webdocNote ? (
                webdocNote.text ? (
                  <>
                    {/* TES-1312 — beside the note, never in it. */}
                    <AiSummarisedHeadings headings={aiHeadings} />
                    <textarea
                      ref={noteRef}
                      readOnly
                      value={webdocNote.text}
                      rows={Math.min(
                        18,
                        webdocNote.text.split("\n").length + 1,
                      )}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label="Journalanteckning för Webdoc"
                      className="w-full rounded-md border border-surfaceMuted bg-surface p-sm font-mono text-xs leading-relaxed text-textPrimary"
                    />
                    {webdocNote.skipped.length > 0 ? (
                      <p className="text-xs text-textSecondary">
                        {`Tomma rubriker togs inte med: ${webdocNote.skipped.join(", ")}.`}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void copyNote()}
                      className="self-start rounded-md bg-brand px-md py-sm text-sm font-semibold text-textOnBrand hover:opacity-90"
                    >
                      Kopiera hela journalanteckningen
                    </button>
                    {copyState !== "idle" ? (
                      <p
                        className="text-xs text-textSecondary"
                        aria-live="polite"
                      >
                        {copyState === "copied"
                          ? "Kopierad — klistra in i Webdoc."
                          : "Kunde inte kopiera automatiskt. Texten är markerad — kopiera den och klistra in i Webdoc."}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-textSecondary">
                    Inget att kopiera — alla rubriker är tomma. Skriv under rätt
                    rubrik innan förnyelsen slutförs.
                  </p>
                )
              ) : null}
            </div>

            {/* TES-997 (Ö-255) — "Slutför i Webdoc innan du publicerar", the
                step before the publish. Only "Nytt recept är utfärdat i
                Webdoc" registers a prescription; "Recept kan inte utfärdas
                ännu" leaves the review open with no new period. TES-1034 —
                with the journal tick, offered once the compiled note is
                shown. */}
            <WebdocControlStep
              idPrefix={`renewal-${cycleId}`}
              prescription={prescription}
              onPrescriptionChange={setPrescription}
              journal={
                webdocNote?.text
                  ? { done: journalDone, onChange: setJournalDone }
                  : undefined
              }
              labOrder={needsLabOrder}
              labOrderDone={labOrderDone}
              onLabOrderDoneChange={setLabOrderDone}
              requisitionRef={{
                value: requisitionRef,
                onChange: setRequisitionRef,
              }}
              disabled={busy}
            />
          </DecisionSection>

          <DecisionSection section="preview" testId={`renewal-${cycleId}`}>
            {/* A-02 §7 — what the outcome changes for her, before the
                publish. */}
            {effects ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-xs text-sm">
                <dt className="text-textSecondary">Din behandling</dt>
                <dd>{effects.dinBehandling}</dd>
                <dt className="text-textSecondary">Hem</dt>
                <dd>{effects.hem}</dd>
                <dt className="text-textSecondary">Kommentaren</dt>
                <dd>{effects.comment}</dd>
              </dl>
            ) : (
              <p className="text-sm text-textSecondary">
                Välj ett utfall för att se vad som ändras för patienten.
              </p>
            )}

            {/* TES-506 — the last step, ONE step. The walkthrough of
                2026-09-04:
                "Den behöver inte granskas, men som sista steg när man läser en månadsincheckning skriver man feedbacken och markerar recept som förnyade."
                TES-1291 — one button for the chosen outcome. */}
            <div className="flex flex-wrap items-center gap-sm">
              <button
                type="button"
                disabled={busy || blocker !== null}
                title={blocker ?? undefined}
                onClick={publishOutcome}
                className={`rounded-md px-md py-sm text-sm text-white hover:opacity-90 disabled:opacity-50 ${
                  outcome === "reject" ? "bg-danger" : "bg-brand"
                }`}
              >
                {outcome === "reject" ? "Avslå" : PUBLISH_TO_PATIENT_CTA}
              </button>
              {blocker && outcome ? (
                <span className="text-xs text-textSecondary">{blocker}</span>
              ) : null}
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-textSecondary">
                Fler åtgärder
              </summary>
              <div className="mt-sm flex flex-wrap items-center gap-sm">
                <button
                  type="button"
                  disabled={
                    busy ||
                    webdocBlocker !== null ||
                    !orderReady ||
                    !prescriptionPayload ||
                    keepWishOpen
                  }
                  title={
                    keepWishOpen
                      ? "Patientens önskemål om att behålla vikten kräver ett fasbeslut, som publiceras med återkopplingen"
                      : webdocBlocker
                        ? webdocBlocker
                        : !orderReady
                          ? "Fyll i läkemedel, dos och frekvens"
                          : !prescriptionPayload
                            ? "Välj receptstatus i Webdoc"
                            : undefined
                  }
                  onClick={() =>
                    void run((webdoc) =>
                      approveRenewal(patientId, cycleId, {
                        ...toOrder(order),
                        needsNewLabs,
                        prescription: prescriptionPayload!,
                        webdocControl: webdoc.webdocControl,
                      }),
                    )
                  }
                  className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                >
                  Bekräfta utan återkoppling
                </button>
                {/* TES-863 (Ö-228) — a correction is its OWN button, not a
                    checkbox on the dose button. A checkbox left ticked from
                    last time does the wrong thing silently, and the two
                    actions mean different things to the patient:
                    "en dosändring är ett kliniskt beslut, en rättelse är ett tangentnedslag som aldrig var ett beslut"
                    The reason is required by the contract, so the button
                    waits for the note — otherwise the server would answer
                    with a validation error on a field the doctor was never
                    shown. */}
                <button
                  type="button"
                  disabled={busy || !orderReady || !trimmedReason}
                  title={
                    !orderReady
                      ? "Fyll i läkemedel, dos och frekvens"
                      : !trimmedReason
                        ? "Skriv skälet i rutan Skäl — en rättelse kräver det"
                        : "Patienten får se ”Din ordination har rättats” och ett prioriterat ärende skapas för direktkontakt"
                  }
                  onClick={() =>
                    void run(
                      () =>
                        adjustDose(patientId, cycleId, {
                          ...toOrder(order),
                          correction: true,
                          reason: trimmedReason,
                        }),
                      false,
                    )
                  }
                  className="rounded-md border border-warning/50 px-md py-sm text-sm text-warning hover:bg-warning/10 disabled:opacity-50"
                >
                  Rätta felskriven ordination
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => markLabNotNeeded(patientId, cycleId, {}),
                      false,
                    )
                  }
                  className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                >
                  Inga prover behövs
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () =>
                        requestFollowUp(patientId, {
                          toRole: "coach",
                          priority: "medium",
                          reason:
                            trimmedReason || "Uppföljning begärd av läkare",
                        }),
                      false,
                    )
                  }
                  className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                >
                  Begär uppföljning
                </button>
              </div>
            </details>
          </DecisionSection>
        </>
      )}
    </div>
  );
}
